<script lang="ts">
export default {
  name: 'CTableSelect',
}

interface Props {
  modelValue: string | number
  options: SelectMixedOption[]
  readonly?: boolean
  optionRenderItems?: (info: { node: VNode; option: SelectBaseOption; selected: boolean }) => VNode[]
  labelTextColor?: string
  labelBackgroundColor?: string
  labelArrowColor?: string
}
</script>

<script lang="ts" setup>
import { type VNode, h, ref, computed } from 'vue'
import useThemeVars from '@lib/composables/useThemeVars'
import CSelect from '@lib/components/CSelect/CSelect.vue'
import ClickOutsideDirective from '@lib/directives/clickOutside'
import type { SelectMixedOption, SelectBaseOption } from 'naive-ui/lib/select/src/interface'

interface Emits {
  (e: 'update:modelValue', value: string | number): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const themeVars = useThemeVars()
const vClickOutside = ClickOutsideDirective
const showSelectMenu = ref(false)
const cursor = computed(() => (props.readonly ? 'default' : 'pointer'))

function onClick() {
  if (props.readonly) return

  showSelectMenu.value = !showSelectMenu.value
}

const renderOption = (info: { node: VNode; option: SelectBaseOption; selected: boolean }) =>
  h(
    'div',
    {
      class: 'flex items-center gap-2 p-2 cursor-pointer c-table-select__option',

      onMouseenter: (event: MouseEvent) => {
        const element = event.target as HTMLElement
        if (element) {
          element.style.background = themeVars.value.primary6
        }
      },
      onMouseleave: (event: MouseEvent) => {
        const element = event.target as HTMLElement
        if (element) {
          element.style.background = themeVars.value.base
        }
      },

      onClick: (event: MouseEvent) => {
        processOptionSelection(event, info)
      },
      onTouchend: (event: TouchEvent) => {
        processOptionSelection(event, info)
      },
    },
    props.optionRenderItems
      ? props.optionRenderItems(info)
      : [h('div', { class: 'font-size-4' }, info.option.label as string)],
  )

function processOptionSelection(event: Event, info: { node: VNode; option: SelectBaseOption; selected: boolean }) {
  event.preventDefault() // Prevent firing click event after touch
  showSelectMenu.value = false
  emit('update:modelValue', info.option.value!)
}
</script>

<template>
  <div
    v-click-outside="() => (showSelectMenu = false)"
    class="w-fit flex items-center justify-center text-truncate rd uppercase c-table-select"
  >
    <CSelect
      :options="options"
      :renderOption="!readonly ? renderOption : null"
      :show="showSelectMenu"
      :showArrow="!readonly"
      :value="modelValue"
      @click="onClick"
    />
  </div>
</template>

<style lang="scss" scoped>
:deep(.n-select) {
  background: v-bind('labelBackgroundColor ?? themeVars.primary6');
}
:deep(.n-base-selection .n-base-suffix .n-base-suffix__arrow) {
  color: v-bind('labelArrowColor ?? `var(--n-arrow-color)`');
}
:deep(.n-base-selection .n-base-selection-label .n-base-selection-input) {
  color: v-bind('labelTextColor ?? `var(--n-text-color)`');
  cursor: v-bind('cursor');
}
:deep(.n-base-selection .n-base-suffix) {
  cursor: v-bind('cursor');
}
.c-table-select:hover {
  background: v-bind('themeVars.primary6');
}
</style>
